import type { AxisDemand } from './drives'
import { JOINT_LABELS } from './drives'
import type { DynamicsResult } from './dynamics'
import type { SimState } from './state'

/**
 * Der Antriebsstrang zwischen Gelenk und Akku.
 *
 * WARUM ES DIESE DATEI ÜBERHAUPT GIBT. Die inverse Dynamik ist verlustfrei. Sie
 * sagt, welches Moment bei welcher Winkelgeschwindigkeit anliegt, und daraus
 * folgt eine mechanische Leistung, die beim Bremsen negativ wird. Wer diese
 * Reihe über den Zyklus aufsummiert, bekommt für das Gehen eine Zahl nahe null
 * — der Roboter würde umsonst laufen. Das ist keine Auslegung, das ist die
 * untere Schranke einer Auslegung.
 *
 * Zwischen der Gelenkleistung und dem Akku stehen drei Dinge, und jedes ist
 * hier ein Schalter:
 *
 *  · FEDER JE GELENK (`recSpring`) — ein Element in Reihe zum Antrieb nimmt
 *    einen Teil der Bremsleistung auf und gibt ihn kurz darauf wieder ab.
 *    Schnell und verlustarm, aber nur im EIGENEN Gelenk, begrenzt im Inhalt und
 *    mit einer Entladezeitkonstante. Das ist der Weg, den die Sehne geht.
 *
 *  · ZWISCHENKREIS (`recBus`) — was die Feder nicht nimmt, geht generatorisch
 *    über den Motor auf den gemeinsamen Kreis. Verlustbehafteter, dafür
 *    GEMEINSAM: jedes Gelenk kann zu jedem späteren Zeitpunkt daraus schöpfen.
 *
 *  · HALTEN (`hold`) — ein Moment zu halten kostet Strom, auch ohne Bewegung.
 *    Kupferverlust in der Wicklung, dazu die Grundlast für Rechner, Regelung
 *    und Kühlung. Diese Energie ist grundsätzlich NICHT rückgewinnbar; sie ist
 *    der Grund, warum ein Roboter auch im Stand Strom zieht.
 *
 * Alle drei aus, und man sieht wieder die verlustfreie Bilanz. Genau darum sind
 * es Schalter und keine Konstanten.
 *
 * WAS DIE RECHNUNG SAGT, und es ist nicht das Erwartete. Nettoarbeit je Zyklus
 * im AUFGEZEICHNETEN Betrieb, in Joule:
 *
 *                     Gehen   Laufen   Hackensprung
 *   alle drei an        128      97          745
 *   ohne die Feder      114      97          669
 *
 * Im gerechneten Gehen dasselbe Vorzeichen bei anderen Zahlen: 119 gegen 90.
 *
 * DIE FEDER KOSTET NEBEN EINEM ZWISCHENKREIS ENERGIE — in jeder Bewegung, in
 * der sie überhaupt etwas ändert.
 *
 * Die naheliegende Erklärung wäre die Zeitkonstante: die Feder zerfällt mit
 * 0,5 s, und über einen Gehzyklus von 1,1 s bleiben davon 12 %. Dann müsste sie
 * sich beim Sprung lohnen, wo Aufnahme und Abgabe dicht beieinanderliegen —
 * genau dafür ist eine Sehne gebaut. Sie lohnt sich auch dort nicht.
 *
 * Der Grund ist die REIHENFOLGE. Die Feder greift ZUERST zu und nimmt 55 % der
 * Bremsleistung. Was sie nimmt, gibt sie zu 80 % und nur im eigenen Gelenk
 * zurück; dieselbe Leistung ginge sonst auf den Zwischenkreis, der nicht
 * zerfällt und aus dem JEDES Gelenk zu JEDEM späteren Zeitpunkt schöpfen kann.
 * Jeder Anteil, der am besseren Speicher vorbeigeht, ist ein Verlust.
 *
 * FÜR SICH GENOMMEN IST DIE FEDER GUT: beim Sprung holt sie 353 J zurück, mehr
 * als in jeder anderen Bewegung. Sie verliert nur das Rennen gegen einen
 * Speicher, der nichts verliert.
 *
 * Das ist genau die Sorte Auskunft, für die die Schalter da sind — und ein
 * Ergebnis, das man mit einer festen Konstante nie gefunden hätte.
 *
 * WO DIESE RECHNUNG VON DER VORLAGE ABWEICHT, und warum:
 *
 *  1. Die Vorlage rechnet die Leistung nur über Sprunggelenk, Knie und Hüfte.
 *     Hier zählen alle Achsen mit, für die es eine Winkelfreiheit gibt — auch
 *     Schulter und Ellbogen. Ein Armschwung kostet Energie; ihn wegzulassen
 *     macht die Bilanz nicht einfacher, nur kleiner.
 *  2. Der Kupferverlust wird nur über Ebenen gebildet, in denen tatsächlich ein
 *     Antrieb sitzt. Ein Moment, das die Struktur trägt, fliesst durch kein
 *     Kupfer. Die Vorlage nimmt den Betrag über alle drei Ebenen und rechnet
 *     damit auch dort Wärme, wo es keinen Motor gibt.
 */

/** Die Kennzahlen des Antriebsstrangs — als Daten, damit sie sich prüfen lassen. */
export interface RecuperationParams {
  /** Anteil der Bremsleistung, den die Feder aufnimmt. */
  beta: number
  /** Rückgabewirkungsgrad der Feder. */
  eta: number
  /** Speicherinhalt je Gelenk, J. */
  cap: number
  /** Entladezeitkonstante der Feder, s. */
  tau: number
  /** Motor als Generator auf den Zwischenkreis. */
  gen: number
  /** Zwischenkreis zurück in mechanische Leistung. */
  inv: number
  /** Kupferverlust je Nm² Haltemoment, W/Nm². */
  cu: number
  /** Grundlast für Elektronik, Rechner und Kühlung, W. */
  idle: number
}

export const RECUPERATION: RecuperationParams = {
  beta: 0.55,
  eta: 0.8,
  cap: 60,
  tau: 0.5,
  gen: 0.85,
  inv: 0.85,
  cu: 0.0012,
  idle: 60,
}

/** Was ein einzelnes Gelenk im Zyklus verlangt und zurückgibt. */
export interface JointPower {
  joint: string
  side: string
  label: string
  /** Mechanische Leistung am Abgang, W. Positiv = treibend. */
  mechanical: Float32Array
  /** Was der Antriebsstrang liefern muss, W. Nie negativ. */
  net: Float32Array
  /** Treibende Arbeit je Zyklus, J. */
  workDriving: number
  /** Bremsarbeit je Zyklus, Betrag, J. */
  workBraking: number
  /** Davon aus der eigenen Feder gedeckt, J. */
  fromSpring: number
  /** Davon aus dem Zwischenkreis gedeckt, J. */
  fromBus: number
  /** Kupferverlust je Zyklus, J. */
  copperLoss: number
  /** Nettoarbeit je Zyklus, J. */
  work: number
}

export interface PowerResult {
  samples: number
  cycleTime: number
  /**
   * Was der Akku liefern muss, W je Abtastpunkt — einschliesslich Grundlast.
   *
   * DIE REIHE, AUF DIE ES ANKOMMT. Aus ihr folgen Akkugrösse, Zwischenkreis und
   * Kühlung; die Spitzen einzelner Achsen folgen daraus nicht.
   */
  net: Float32Array
  /** Grundlast und Kupferverlust, W je Abtastpunkt. */
  holding: Float32Array
  joints: JointPower[]
  /** Summe der treibenden Arbeit aller Achsen, J. */
  workDriving: number
  /** Summe der Bremsarbeit aller Achsen, Betrag, J. */
  workBraking: number
  /** Aus den Federn zurückgeholt, J. */
  fromSpring: number
  /** Aus dem Zwischenkreis zurückgeholt, J. */
  fromBus: number
  /** Halte- und Elektronikarbeit, J. */
  holdWork: number
  /** Nettoarbeit je Zyklus, J — das, was der Akku zahlt. */
  work: number
  /** Mittlere Nettoleistung, W. */
  meanPower: number
  /** Grösste Nettoleistung, W. */
  peakPower: number
  /**
   * Anteil der treibenden Arbeit, der aus einem Speicher kam, 0 … 1.
   *
   * Die eine Zahl, an der man die drei Schalter misst. Ohne Speicher ist sie
   * null, und die Nettoarbeit ist die volle treibende Arbeit.
   */
  recoveredShare: number
  /**
   * Transportkosten, dimensionslos: Energie je Kilogramm und Meter, auf g bezogen.
   *
   * Die Vergleichsgrösse zwischen Maschinen unterschiedlicher Grösse. Ein
   * gehender Mensch liegt bei rund 0,2 … 0,3.
   */
  transportCost: number
}

const G = 9.81

/** Wie oft der Speicherstand eingeschwungen wird, bevor gemessen wird. */
const SETTLE_PASSES = 3

/**
 * Rechnet den Antriebsstrang über einen Zyklus.
 *
 * DER SPEICHERSTAND WIRD EINGESCHWUNGEN. Die Feder eines Gelenks trägt Energie
 * über die Zyklusgrenze; startete die Rechnung bei leerem Speicher, wäre der
 * erste Zyklus teurer als jeder folgende und die Lösung nicht periodisch. Es
 * wird deshalb mehrfach durchlaufen und erst im letzten Durchgang gezählt.
 */
export function computePower(
  state: SimState,
  dynamics: DynamicsResult,
  demands: AxisDemand[],
  totalMass: number,
  stride: number,
  params: RecuperationParams = RECUPERATION,
): PowerResult {
  const n = dynamics.samples
  const dt = dynamics.cycleTime / n

  /* Nach Gelenk und Seite bündeln: die Feder sitzt am GELENK, nicht an einer
     Ebene. Drei Antriebe eines Hüftgelenks teilen sich denselben Speicher. */
  const groups = new Map<string, { joint: string; side: string; axes: AxisDemand[] }>()
  for (const demand of demands) {
    if (demand.structural || demand.powerTrack.length !== n) continue
    const key = `${demand.joint}|${demand.side}`
    const group = groups.get(key)
    if (group) group.axes.push(demand)
    else groups.set(key, { joint: demand.joint, side: demand.side, axes: [demand] })
  }

  const joints: JointPower[] = []
  /* Das Moment, das der Antrieb hält — je Gelenk parallel geführt und nicht in
     der Schnittstelle: es ist eine Zwischengrösse dieser Rechnung, und wer sie
     von aussen liest, hält sie für ein Gelenkmoment. Das steht in der Dynamik. */
  const heldMoment: Float32Array[] = []

  for (const group of groups.values()) {
    const mechanical = new Float32Array(n)
    for (const axis of group.axes) {
      for (let i = 0; i < n; i++) mechanical[i] += axis.powerTrack[i]
    }
    /* Der Betrag des Moments über die Ebenen, in denen ein Antrieb sitzt — die
       Grösse, die den Strom bestimmt und damit den Kupferverlust. */
    const held = new Float32Array(n)
    for (let i = 0; i < n; i++) {
      let sum = 0
      for (const axis of group.axes) {
        const m = axis.momentTrack[i]
        sum += m * m
      }
      held[i] = Math.sqrt(sum)
    }
    heldMoment.push(held)
    joints.push({
      joint: group.joint,
      side: group.side,
      label: `${JOINT_LABELS[group.joint] ?? group.joint} ${group.side}`,
      mechanical,
      net: new Float32Array(n),
      workDriving: 0,
      workBraking: 0,
      fromSpring: 0,
      fromBus: 0,
      copperLoss: 0,
      work: 0,
    })
  }

  const holding = new Float32Array(n)
  const net = new Float32Array(n)

  const useSpring = state.features.recSpring
  const useBus = state.features.recBus
  const useHold = state.features.hold

  const leak = Math.exp(-dt / params.tau)
  const store = new Float64Array(joints.length)
  let bus = 0

  for (const joint of joints) {
    for (let i = 0; i < n; i++) {
      if (joint.mechanical[i] > 0) joint.workDriving += joint.mechanical[i] * dt
      else joint.workBraking -= joint.mechanical[i] * dt
    }
  }

  for (let pass = 0; pass <= SETTLE_PASSES; pass++) {
    const counting = pass === SETTLE_PASSES
    if (counting) {
      for (const joint of joints) {
        joint.fromSpring = 0
        joint.fromBus = 0
        joint.copperLoss = 0
      }
    }

    for (let i = 0; i < n; i++) {
      /* 1. Laden — erst die Feder im Gelenk, der Rest generatorisch auf den Kreis. */
      for (let j = 0; j < joints.length; j++) {
        store[j] *= leak
        const brake = Math.max(-joints[j].mechanical[i], 0)
        if (brake <= 0) continue
        let toSpring = 0
        if (useSpring) {
          toSpring = Math.min(brake * params.beta, Math.max(0, params.cap - store[j]) / dt)
          store[j] += toSpring * dt
        }
        const rest = brake - toSpring
        if (useBus && rest > 0) bus += rest * params.gen * dt
      }

      /* 2. Entladen — jedes Gelenk deckt seinen Bedarf erst aus der eigenen
            Feder, dann aus dem gemeinsamen Kreis, den Rest zahlt der Akku. */
      let total = 0
      for (let j = 0; j < joints.length; j++) {
        const joint = joints[j]
        let need = Math.max(joint.mechanical[i], 0)

        if (useSpring && need > 0 && store[j] > 0) {
          const take = Math.min(need, (store[j] * params.eta) / dt)
          store[j] -= (take * dt) / params.eta
          need -= take
          if (counting) joint.fromSpring += take * dt
        }
        if (useBus && need > 0 && bus > 0) {
          const take = Math.min(need, (bus * params.inv) / dt)
          bus -= (take * dt) / params.inv
          need -= take
          if (counting) joint.fromBus += take * dt
        }
        /* Kupferverlust: Strom fliesst auch ohne Bewegung, solange ein Moment
           gehalten wird. Er kommt AUF den Bedarf, nicht statt seiner. */
        if (useHold) {
          const held = heldMoment[j][i]
          const copper = params.cu * held * held
          need += copper
          if (counting) joint.copperLoss += copper * dt
        }
        joint.net[i] = need
        total += need
      }

      const idle = useHold ? params.idle : 0
      if (counting) {
        holding[i] = idle
        net[i] = total + idle
      }
    }
  }

  let workDriving = 0
  let workBraking = 0
  let fromSpring = 0
  let fromBus = 0
  let copper = 0
  for (const joint of joints) {
    joint.work = 0
    for (let i = 0; i < n; i++) joint.work += joint.net[i] * dt
    workDriving += joint.workDriving
    workBraking += joint.workBraking
    fromSpring += joint.fromSpring
    fromBus += joint.fromBus
    copper += joint.copperLoss
  }

  let work = 0
  let peak = 0
  for (let i = 0; i < n; i++) {
    work += net[i] * dt
    peak = Math.max(peak, net[i])
  }

  const holdWork = copper + (useHold ? params.idle * dynamics.cycleTime : 0)
  const distance = Math.max(1e-6, stride)

  return {
    samples: n,
    cycleTime: dynamics.cycleTime,
    net,
    holding,
    joints,
    workDriving,
    workBraking,
    fromSpring,
    fromBus,
    holdWork,
    work,
    meanPower: dynamics.cycleTime > 0 ? work / dynamics.cycleTime : 0,
    peakPower: peak,
    recoveredShare: workDriving > 0 ? (fromSpring + fromBus) / workDriving : 0,
    transportCost: work / (totalMass * G * distance),
  }
}
